import { useForm } from "@mantine/form";
import {
  InspectionsGet200ResponseRowsInner,
  InspectionsGetRequestInspectionTypeEnum,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner,
  SpcApiInspectionsInspectionIdDeleteRequest,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
  SpcApiInspectionsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SpcInstance } from "@/instance/axios";
import { InspectionMutations } from "@/api/inspect/useInspectQuery";
import { customNotification } from "@/utils/notificationShow";

interface UseInspectFormParams {
  itemCode?: string;
  lotId?: number;
  wmsLogId?: number;
  outgoing?: boolean;
  inspectionType?: InspectionsGetRequestInspectionTypeEnum;
  workId?: number;
}

export const useInspectForm = ({
  itemCode,
  lotId,
  wmsLogId,
  outgoing = false,
  inspectionType,
  workId,
}: UseInspectFormParams) => {
  const [specificationId, setSpecificationId] = useState<number>(0);
  const [specificationPostData, setSpecificationPostData] =
    useState<InspectionsGet200ResponseRowsInner>();
  const [incomingInspectBoolean, setIncomingInspectBoolean] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const resolvedInspectionType =
    inspectionType ??
    (outgoing
      ? InspectionsGetRequestInspectionTypeEnum.OUTGOING
      : InspectionsGetRequestInspectionTypeEnum.INCOMING);

  const form = useForm<SpcApiInspectionsPostRequest>({
    initialValues: {
      inspectionsGetRequest: {
        itemCode,
        lotId,
        inspectionType: resolvedInspectionType,
        specificationId,
        wmsLogId,
      },
    },
  });

  const recordForm = useForm<SpcApiInspectionsInspectionIdSubmitPostRequest>({
    initialValues: {
      inspectionId: specificationPostData?.id ?? 0,
      inspectionsInspectionIdSubmitPostRequest: {
        description: undefined,
        temQuantity: undefined,
        record: [
          [],
        ] as ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner[][],
      },
    },
  });

  useEffect(() => {
    form.setFieldValue(
      "inspectionsGetRequest.specificationId",
      specificationId,
    );
  }, [specificationId]);

  useEffect(() => {
    if (
      resolvedInspectionType !==
      InspectionsGetRequestInspectionTypeEnum.IN_PROCESS
    ) {
      return;
    }
    if (!workId) {
      if (specificationPostData) {
        setSpecificationPostData(undefined);
        setIncomingInspectBoolean(false);
        setSpecificationId(0);
      }
      return;
    }
    if (
      specificationPostData?.workId &&
      specificationPostData.workId !== workId
    ) {
      setSpecificationPostData(undefined);
      setIncomingInspectBoolean(false);
      setSpecificationId(0);
    }
  }, [resolvedInspectionType, specificationPostData?.workId, workId]);

  useEffect(() => {
    if (
      resolvedInspectionType !==
        InspectionsGetRequestInspectionTypeEnum.IN_PROCESS ||
      !workId ||
      specificationPostData?.id
    ) {
      return;
    }
    loadExistingInspection();
  }, [resolvedInspectionType, specificationPostData?.id, workId]);

  useEffect(() => {
    if (!specificationPostData?.specificationData?.criteria) return;

    const recordInit = specificationPostData.specificationData.criteria.map(
      (criteria) =>
        Array.from({ length: criteria.sampleNum ?? 1 }).map(() => {
          let defaultValue: string | number | boolean | null;
          let isPassed: boolean = null as unknown as boolean;
          switch (criteria.type) {
            case "boolean":
              defaultValue = false;
              break;
            case "number":
              defaultValue = null;
              break;
            case "select":
              defaultValue = null;
              break;
            case "description":
            default:
              defaultValue = null;
              isPassed = false;
              break;
          }

          return { value: defaultValue, isPassed: isPassed };
        }),
    );

    recordForm.setValues({
      inspectionId: specificationPostData.id ?? 0,
      inspectionsInspectionIdSubmitPostRequest: {
        description: undefined,
        temQuantity: undefined,
        record: recordInit,
      },
    });
  }, [specificationPostData]);

  const loadExistingInspection = async () => {
    try {
      const response = await SpcInstance.inspectionsGet({
        populate: ["subInspections"],
        sort: "-id",
        page: 1,
        pageSize: 1,
        query: JSON.stringify({
          workId,
          inspectionType: resolvedInspectionType,
        }),
      });
      const [latestInspection] = response.data?.rows ?? [];
      if (latestInspection) {
        setSpecificationId(latestInspection.specificationId);
        setSpecificationPostData(latestInspection);
        setIncomingInspectBoolean(true);
      }
    } catch (error) {
      console.error("Failed to load existing process inspection", error);
    }
  };

  const { mutate: createInspection } = useMutation({
    mutationFn: (params: SpcApiInspectionsPostRequest) =>
      InspectionMutations.create(params as SpcApiInspectionsPostRequest),
    onSuccess: (data) => {
      setSpecificationPostData(data?.data);
      setIncomingInspectBoolean(true);

      customNotification.success({
        message: "검사 기록이 생성되었습니다.",
      });
    },
    onError: (error) => {
      customNotification.error({
        message: "검사 기록 생성에 실패했습니다.",
      });
    },
  });

  const { mutate: deleteInspection } = useMutation({
    mutationFn: (params: SpcApiInspectionsInspectionIdDeleteRequest) =>
      InspectionMutations.delete(
        params as SpcApiInspectionsInspectionIdDeleteRequest,
      ),
    onSuccess: () => {
      setSpecificationPostData(undefined);
      setIncomingInspectBoolean(false);
      recordForm.setValues({
        inspectionId: 0,
        inspectionsInspectionIdSubmitPostRequest: {
          description: undefined,
          temQuantity: undefined,
          record: [[]],
        },
      });

      customNotification.success({
        message: "검사 기록이 삭제되었습니다.",
      });
    },
    onError: (error) => {
      customNotification.error({
        message: "검사 기록 삭제에 실패했습니다.",
      });
    },
  });

  const { mutate: submitInspectionMutation } = useMutation({
    mutationFn: (params: SpcApiInspectionsInspectionIdSubmitPostRequest) =>
      InspectionMutations.submit(
        params as SpcApiInspectionsInspectionIdSubmitPostRequest,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });

      customNotification.success({
        message: "검사 기록이 제출되었습니다.",
      });
    },
    onError: (error) => {
      customNotification.error({
        message: "검사 기록 제출에 실패했습니다.",
      });
    },
  });

  const handleSubmitInspect = () => {
    submitInspectionMutation(recordForm.values);
  };

  return {
    specificationId,
    setSpecificationId,
    specificationPostData,
    setSpecificationPostData,
    incomingInspectBoolean,
    recordForm,
    createInspection,
    deleteInspection,
    handleSubmitInspect,
  };
};
